import { abs, dot, float, max, smoothstep, texture, uniform, vec2, vec4 } from 'three/tsl';
import * as THREE from 'three/webgpu';

type N = any;

/**
 * Ground the map covers (m, a square centred under the camera: the trees' draw distance, 520 m,
 * either way) and its resolution (1 m texels: a crown is 5 to 7 across). Less in a small world:
 * see the constructor.
 */
const SIZE = 1024;
const RES = 1024;
/** The map is drawn from this far above the camera, looking down. */
const ABOVE = 3000;
/**
 * The map's height channel is cleared to this (far below any crown), so max blending keeps the
 * highest crown's underside over each texel. Heights are camera-relative, precise near the
 * camera. (An offset of 300 m instead clamped crowns more than 300 m below the camera, on a
 * mountain over a forest, to the cleared value, and they shaded their own tops.)
 */
const NO_CROWN = -60000;
const _clear = new THREE.Color(0, NO_CROWN, 0);
/** How much of the sun a crown keeps off the ground under its middle (leaves let some through). */
const CANOPY_DEPTH = 0.8;

const _y = new THREE.Vector3(0, 1, 0);
const _x = new THREE.Vector3(1, 0, 0);
const _u = new THREE.Vector3();
const _v = new THREE.Vector3();

/**
 * The shade of tree crowns. The sun is at the hollow's centre, so every crown's shadow
 * lies straight under it: drawn from straight above as soft discs, the crowns make a map
 * of where the sun is kept off the ground. Each texel holds how much sun the crowns there
 * stop (max of them) and the height of the highest crown's underside: a point is shaded
 * only below it, so a crown never shades its own top. LightRig multiplies the sunlight by
 * it (like the clouds' shadow), so the ground, grass, rocks and lower trees all take it.
 *
 * Everything is in render space (camera-relative): the map follows the camera, aimed down
 * the hollow's "up" there. That holds where the ground's up is nearly the camera's: in a small
 * world the map shrinks to stay there, and leaves out the far side, which lies "up" the same
 * axis (Nexus, 400 m across, inside a map 1 km wide: the far side's crowns were drawn straight
 * through the hollow onto the ground by the player, and slid about as they moved).
 */
export class CanopyShadow {
  /** The crowns' discs (see TreeRenderer): drawn into the map by render(). */
  readonly scene = new THREE.Scene();
  private readonly camera: THREE.OrthographicCamera;
  /** Ground the map covers (m). */
  private readonly size: number;
  private readonly target: THREE.RenderTarget;
  private readonly map: N;
  /** The map's axes in render space (its x and y, and up). */
  readonly axisU = uniform(new THREE.Vector3(1, 0, 0));
  readonly axisV = uniform(new THREE.Vector3(0, 0, 1));
  readonly axisUp = uniform(new THREE.Vector3(0, 1, 0));
  private state: N = {};

  /**
   * `radius`: how tightly the world's ground curves (a sphere's radius, a torus's tube). Within
   * 0.4 of it either way the ground's up is within 24° of the camera's, so the shade still falls
   * about where it should. Crowns more than a radius above the camera are left out: the far side.
   */
  constructor(
    private readonly renderer: THREE.WebGPURenderer,
    radius = Number.POSITIVE_INFINITY,
  ) {
    this.size = Math.min(SIZE, 0.8 * radius);
    const half = this.size / 2;
    const reach = Math.min(ABOVE - 1, radius);
    this.camera = new THREE.OrthographicCamera(-half, half, half, -half, ABOVE - reach, 2 * ABOVE);
    this.target = new THREE.RenderTarget(RES, RES, {
      type: THREE.HalfFloatType,
      format: THREE.RGFormat,
      depthBuffer: false,
    });
    this.target.texture.minFilter = THREE.LinearFilter;
    this.target.texture.magFilter = THREE.LinearFilter;
    this.target.texture.generateMipmaps = false;
    this.target.texture.name = 'CanopyShadow';
    this.map = texture(this.target.texture);
  }

  /** What a crown's disc writes (max-blended): its shade, and the height of its underside. */
  crownColor(shade: N, renderPos: N): N {
    return vec4(shade, dot(renderPos, this.axisUp), 0, 1);
  }

  /** Draw the crowns' map, aimed down `up` (the hollow's up at the camera, unit, render space). */
  render(up: THREE.Vector3): void {
    const c = this.camera;
    const helper = Math.abs(up.y) < 0.9 ? _y : _x;
    _u.crossVectors(helper, up).normalize();
    _v.crossVectors(up, _u);
    c.position.copy(up).multiplyScalar(ABOVE);
    c.up.copy(_v);
    c.lookAt(0, 0, 0);
    c.updateMatrixWorld();
    // The camera's own screen axes, so the map is read the way it was drawn.
    this.axisU.value.setFromMatrixColumn(c.matrixWorld, 0);
    this.axisV.value.setFromMatrixColumn(c.matrixWorld, 1);
    this.axisUp.value.copy(up);
    const r = this.renderer;
    this.state = THREE.RendererUtils.resetRendererState(r, this.state);
    r.setClearColor(_clear, 1);
    r.setRenderTarget(this.target);
    r.clear();
    r.render(this.scene, c);
    THREE.RendererUtils.restoreRendererState(r, this.state);
  }

  /** The share of sunlight the crowns let through at a render-space position (1 = none in the way). */
  sample(pw: N): N {
    // A render target's first row is the top of what was drawn (WebGPU): v runs down.
    const uv = vec2(dot(pw, this.axisU), dot(pw, this.axisV).negate()).div(this.size).add(0.5);
    const m = this.map.sample(uv).level(0);
    const h = dot(pw, this.axisUp);
    // Shaded only a little below the highest crown's underside there.
    const under = smoothstep(m.g.sub(0.3), m.g.sub(1.3), h);
    // Fades out toward the map's edge (crowns further off cast no shade).
    const inside = float(1).sub(smoothstep(0.4, 0.5, max(abs(uv.x.sub(0.5)), abs(uv.y.sub(0.5)))));
    return float(1).sub(m.r.mul(under).mul(inside).mul(CANOPY_DEPTH));
  }
}
